import type { Cart } from '@diamond/shared/types';
import { selectCartItems } from '@diamond/sol-buyer/utils';
import { useEffect } from 'react';

interface UsePendingCartAutoSelectArgs {
  pendingProductIds: string[];
  cartItems: Cart[];
  cartItemIds: string[];
  addCartItem: (cartItem: Cart) => void;
  removeAutoSelectProductIds: (productIds: string[]) => void;
}

/**
 * Hook to automatically select cart items that were queued for auto-selection.
 * This is used when items are added to cart from product detail page and should
 * be automatically checked in the cart page.
 */
export const usePendingCartAutoSelect = ({
  pendingProductIds,
  cartItems,
  cartItemIds,
  addCartItem,
  removeAutoSelectProductIds,
}: UsePendingCartAutoSelectArgs) => {
  useEffect(() => {
    if (pendingProductIds.length === 0 || cartItems.length === 0) {
      return;
    }

    const productSet = new Set(pendingProductIds);
    const currentSelected = new Set(cartItemIds);
    const cartIdsToSelect: string[] = [];
    const matchedProductIds = new Set<string>();

    cartItems.forEach((item) => {
      if (!productSet.has(item.product.id)) {
        return;
      }

      matchedProductIds.add(item.product.id);
      if (!currentSelected.has(item.id)) {
        cartIdsToSelect.push(item.id);
      }
    });

    if (cartIdsToSelect.length > 0) {
      const itemsToSelect = selectCartItems(cartIdsToSelect, cartItems);
      itemsToSelect.forEach(addCartItem);
    }

    if (matchedProductIds.size > 0) {
      removeAutoSelectProductIds(Array.from(matchedProductIds));
    }
  }, [
    pendingProductIds,
    cartItems,
    cartItemIds,
    addCartItem,
    removeAutoSelectProductIds,
  ]);
};
